// File: src/views/apps/account/customer-group/CreateCustomerGroup.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Label,
  Form,
  Alert,
  Spinner,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import swal from "sweetalert";

import { _Get, _PostSave, _Put } from "../import/ApiEndPoint/ApiCalling";

import {
  Create_CustomerGroup,
  Update_CustomerGroup_by_id,
  View_CustomerGroup,
  View_CustomerGroup_by_id,
  PurchaseProductList_Product,
  Product_Price_Bulk_Update,
} from "../import/ApiEndPoint/Api";

// ============ Inline CSS (once) ============
const injectOnce = () => {
  if (document.getElementById("cge-inline-css")) return;
  const s = document.createElement("style");
  s.id = "cge-inline-css";
  s.textContent = `
    .cge-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .cge-card .card-body{ padding:12px; }
    .cge-toolbar{ padding:8px; background:#f4f7f6; border-radius:14px; border:1px solid #e5e7eb; margin-bottom:10px; }
    .cge-title{ font-weight:800; text-transform:uppercase; font-size:18px; }
    .cge-pill{ display:inline-block; background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; margin-left:8px; }
    .cge-note{ color:#b91c1c; font-weight:700; margin:8px 0 0; }
    .cge-actions{ display:flex; gap:8px; flex-wrap:nowrap; }
  `;
  document.head.appendChild(s);
};

const toNum = (v) =>
  v === "" || v === null || v === undefined ? "" : Number(v);

// =================================================
export default function CreateCustomerGroup() {
  const navigate = useNavigate();
  const { id } = useParams(); // "0" for create, otherwise edit

  const [loading, setLoading] = useState(false);
  const [updatingProducts, setUpdatingProducts] = useState(false);

  // Form state
  const [form, setForm] = useState({
    GroupName: "",
    Grade: "",
    discountPercentage: "",
  });

  // Context data
  const [maxDiscount, setMaxDiscount] = useState(0); // current highest discount among active groups
  const [products, setProducts] = useState([]); // for bulk recompute if raising max

  // Derived state
  const isEdit = id && String(id) !== "0";
  const isRaisingMax = useMemo(
    () => Number(form.discountPercentage || 0) > Number(maxDiscount || 0),
    [form.discountPercentage, maxDiscount]
  );

  // ============ Effects ============
  useEffect(() => injectOnce(), []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");

    // 1) get all active groups to compute current max
    _Get(View_CustomerGroup, user?.database)
      .then((res) => {
        const active = Array.isArray(res?.CustomerGroup)
          ? res.CustomerGroup.filter((g) => g?.status === "Active")
          : [];
        const max = active.length
          ? active.reduce(
              (m, g) => (Number(g?.discount) > m ? Number(g?.discount) : m),
              0
            )
          : 0;
        setMaxDiscount(max);
      })
      .catch(() => setMaxDiscount(0));

    // 2) load products for potential bulk recompute (only when we might need them)
    _Get(PurchaseProductList_Product, user?.database)
      .then((res) => {
        const list = Array.isArray(res?.Product) ? res.Product : [];
        setProducts(list.reverse());
      })
      .catch(() => setProducts([]));

    // 3) if edit mode, load group
    if (isEdit) {
      _Get(View_CustomerGroup_by_id, id)
        .then((res) => {
          const g = res?.CustomerGroup || {};
          setForm({
            GroupName: g?.groupName || "",
            Grade: g?.grade || "",
            discountPercentage:
              g?.discount === 0 || g?.discount ? String(g.discount) : "",
          });
        })
        .catch(() => {
          swal("Error", "Failed to load group", "error");
          navigate("/app/grades");
        });
    }
  }, [id, isEdit]);

  // ============ Handlers ============
  const onChange = (e) => {
    const { name, value } = e.target;

    if (name === "GroupName") {
      // keep id/groupName aligned, trimmed of spaces
      const cleaned = value.replace(/\s/g, "");
      setForm((p) => ({ ...p, GroupName: cleaned }));
      return;
    }
    setForm((p) => ({ ...p, [name]: value }));
  };

  const recomputeAndBulkUpdate = async (newMax) => {
    // Recompute SalesRate & Product_MRP for each product based on new highest discount
    const recomputed = products.map((p) => {
      const gst = (100 + Number(p?.GSTRate || 0)) / 100;
      const dis = (100 + Number(newMax || 0)) / 100;

      const profitPer =
        Number(p?.ProfitPercentage || 0) > 3 ? Number(p?.ProfitPercentage) : 3;
      const profitFactor = (100 + profitPer) / 100;

      const base = Number(p?.Purchase_Rate || 0) * profitFactor;
      const mrp = base * gst * dis;

      return {
        id: p?._id,
        SalesRate: Number(base.toFixed(2)),
        Product_MRP: Number(mrp.toFixed(2)),
        ProfitPercentage: p?.ProfitPercentage,
        maxDiscount: newMax,
      };
    });

    setUpdatingProducts(true);
    try {
      await _Put(Product_Price_Bulk_Update, undefined, {
        Products: recomputed,
      });
      setUpdatingProducts(false);
      swal(
        "Success",
        "Product prices updated with new max discount",
        "success"
      );
      navigate("/app/grades");
    } catch (err) {
      setUpdatingProducts(false);
      swal("Error", "Bulk price update failed", "error");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.GroupName || !form.Grade || form.discountPercentage === "") {
      return swal("Please fill all required fields");
    }

    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const payload = {
      id: form.GroupName, // short id = name (no spaces)
      groupName: form.GroupName,
      grade: form.Grade,
      discount: Number(form.discountPercentage),
      created_by: user?._id,
      database: user?.database,
    };

    setLoading(true);
    try {
      if (isEdit) {
        await _Put(Update_CustomerGroup_by_id, id, payload);
        if (isRaisingMax) {
          await recomputeAndBulkUpdate(Number(form.discountPercentage));
          return;
        }
        swal("Success", "Group updated successfully", "success");
        navigate("/app/grades");
      } else {
        await _PostSave(Create_CustomerGroup, payload);
        if (isRaisingMax) {
          await recomputeAndBulkUpdate(Number(form.discountPercentage));
          return;
        }
        swal("Success", "Group created successfully", "success");
        navigate("/app/grades");
      }
    } catch (err) {
      swal("Error", "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  // ============ Render ============
  return (
    <>
      {updatingProducts && (
        <Alert color="danger" className="mb-2">
          Updating Product Data… You will be redirected automatically.
          <div style={{ fontWeight: 700, color: "#b91c1c" }}>
            Do not refresh or navigate away.
          </div>
        </Alert>
      )}

      {/* Toolbar */}
      <Row className="cge-toolbar">
        <Col md="6" sm="12" className="d-flex align-items-center">
          <div className="cge-title">
            {isEdit ? "Edit Customer Group" : "Create Customer Group"}
            <span className="cge-pill">
              Current Max: {Number(maxDiscount)}%
            </span>
          </div>
        </Col>
        <Col
          md="6"
          sm="12"
          className="d-flex align-items-center justify-content-end"
        >
          <div className="cge-actions">
            <Button
              outline
              color="secondary"
              onClick={() => navigate("/app/grades")}
            >
              Back
            </Button>
          </div>
        </Col>
      </Row>

      {/* Form Card */}
      <Card className="cge-card">
        <CardBody>
          <Form onSubmit={onSubmit}>
            <Row>
              <Col lg="3" md="4" sm="6" className="mb-2">
                <Label>Group Name *</Label>
                <Input
                  name="GroupName"
                  placeholder="Enter Group Name (no spaces)"
                  value={form.GroupName}
                  onChange={onChange}
                  required
                />
              </Col>

              <Col lg="3" md="4" sm="6" className="mb-2">
                <Label>Grade *</Label>
                <Input
                  name="Grade"
                  placeholder="Enter Grade"
                  value={form.Grade}
                  onChange={onChange}
                  required
                />
              </Col>

              <Col lg="3" md="4" sm="6" className="mb-2">
                <Label>Discount (%) *</Label>
                <Input
                  type="number"
                  name="discountPercentage"
                  placeholder="e.g. 5"
                  value={form.discountPercentage}
                  onChange={onChange}
                  min={0}
                  max={50}
                  step="0.01"
                  required
                />
                {isRaisingMax ? (
                  <div className="cge-note">
                    This exceeds the current max ({maxDiscount}%). Saving will
                    recalculate MRP for all products.
                  </div>
                ) : null}
              </Col>

              <Col lg="3" md="4" sm="6" className="d-flex align-items-end mb-2">
                <Button
                  color="primary"
                  type="submit"
                  disabled={loading || updatingProducts}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : isEdit ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>
    </>
  );
}
